import {
    Box,
    Button,
    // Typography,
    Link,
    Typography,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { useState } from 'react';
import ym from 'react-yandex-metrika';
import { Redirect, Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import LoginField from 'src/components/LoginField';
import PasswordField from 'src/components/PasswordField';
import WidgetWrapper from 'src/components/FormWrapper';
// import TelegramButton from 'src/widgets/TelegramButton'
// import ButtonText from 'src/components/ButtonText'
import useGoTo from 'src/hooks/useGoTo';
import ROUTES from 'src/router/routes';

import scheme, { type TypeRegisterScheme } from 'src/validations/register';
import useSessionStore from 'src/stores/session';
import CustomAccordion from 'src/components/CustomAccordion';

import { ROUTES_URL } from 'src/constants/routes';

import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import SessionService from 'src/services/SessionService';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import TurnstileWidget from 'src/components/TurnstileWidget';
import {
    analyticsConsentChoice,
    setAnalyticsConsentChoice,
    waitForAcquisitionAttribution,
} from 'src/services/AcquisitionAttribution';

import type { SubmitHandler } from 'react-hook-form';

const REGISTER_FIELD_NAMES: Array<keyof TypeRegisterScheme> = ['email', 'password', 'password2'];
const REGISTER_FIELD_ALIASES: Record<string, keyof TypeRegisterScheme> = {
    username: 'email',
};

function extractFirstMessage(value: unknown): string | undefined {
    if (Array.isArray(value)) {
        return extractFirstMessage(value[0]);
    }

    if (typeof value === 'string') {
        return value;
    }

    if (value && typeof value === 'object') {
        for (const nestedValue of Object.values(value)) {
            const message = extractFirstMessage(nestedValue);
            if (message) {
                return message;
            }
        }
    }

    return undefined;
}

function normalizeBackendMessage(message?: string) {
    if (!message) {
        return '';
    }

    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('too common')) {
        return 'Пароль слишком простой. Придумайте более надежный пароль.';
    }

    if (lowerMessage.includes('too short') || lowerMessage.includes('at least 8')) {
        return 'Пароль должен содержать минимум 8 символов';
    }

    if (lowerMessage.includes('entirely numeric') || lowerMessage.includes('only digits')) {
        return 'Пароль не может состоять только из цифр';
    }

    if (lowerMessage.includes('too similar')) {
        return 'Пароль слишком похож на email';
    }

    if (lowerMessage.includes('already exists') || lowerMessage.includes('уже существует')) {
        return 'Пользователь с такой почтой уже зарегистрирован';
    }

    return message;
}

function getRegisterFieldName(key: string) {
    if (REGISTER_FIELD_NAMES.includes(key as keyof TypeRegisterScheme)) {
        return key as keyof TypeRegisterScheme;
    }

    return REGISTER_FIELD_ALIASES[key];
}

export default function RegisterForm() {
    const { enqueueSnackbar } = useSnackbar();
    const goTo = useGoTo();
    const turnstileSiteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
    const [captchaToken, setCaptchaToken] = useState('');
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const [analyticsAccepted, setAnalyticsAccepted] = useState(
        () => analyticsConsentChoice() === 'accepted'
    );

    const session = useSessionStore(({ isAuthorized }) => ({
        isAuthorized,
    }));

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isValid },
    } = useForm<TypeRegisterScheme>({
        resolver: zodResolver(scheme),
        resetOptions: {
            keepIsSubmitted: true,
        },
    });

    const { mutateAsync: login, isLoading: isLoginLoading } = useMutation(
        SessionService.queryKey,
        useSessionStore.getState().login,
        {
            onError: (error: Obj) => {
                const serverMessage = normalizeBackendMessage(extractFirstMessage(error?.detail ?? error));

                enqueueSnackbar(
                    serverMessage || 'Аккаунт создан, но автоматический вход не удался. Попробуйте войти вручную.',
                    { variant: 'error' }
                );
            },
            onSuccess: () => {
                goTo(ROUTES_URL.WELCOME_SCREEN);
            },
        }
    );

    const { mutateAsync: register, isLoading: isRegisterLoading } = useMutation(
        SessionService.queryKey,
        useSessionStore.getState().register,
        {
            onError: (error: Obj) => {
                if (turnstileSiteKey) {
                    setCaptchaToken('');
                    setTurnstileResetKey((key) => key + 1);
                }
                const globalMessages: string[] = [];
                let hasVisibleError = false;

                Object.entries(error || {}).forEach(([key, value]) => {
                    const message = normalizeBackendMessage(extractFirstMessage(value));
                    const fieldName = getRegisterFieldName(key);

                    if (!message) {
                        return;
                    }

                    if (fieldName) {
                        setError(fieldName, { type: 'server', message });
                    } else {
                        globalMessages.push(message);
                    }

                    hasVisibleError = true;
                });

                if (globalMessages.length > 0) {
                    enqueueSnackbar(globalMessages.join(' '), { variant: 'error' });
                }

                if (!hasVisibleError) {
                    enqueueSnackbar('Не удалось создать аккаунт. Проверьте данные и попробуйте еще раз.', {
                        variant: 'error',
                    });
                }
            },
        }
    );

    const onSubmit: SubmitHandler<TypeRegisterScheme> = async (data) => {
        try {
            await waitForAcquisitionAttribution();
            await register({
                ...data,
                username: data.email,
                captcha_token: captchaToken || undefined,
            });
            ym('reachGoal', '350175706');

            await login({ email: data.email, password: data.password });
        } catch (_err) {
            return;
        }
    };

    const isLoading = isRegisterLoading || isLoginLoading;

    if (session.isAuthorized) return <Redirect to={'/'} />;

    return (
        <LoadingWrapper isLoading={isLoading}>
            <WidgetWrapper
                name="Регистрация"
                topGutter
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box mb={2}>
                        <Controller
                            name="email"
                            control={control}
                            disabled={isLoading}
                            render={({ field }) => (
                                <LoginField
                                    {...field}
                                    label="Почта"
                                    placeholder="Введите email"
                                    error={errors?.email?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box mb={2}>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <PasswordField
                                    {...field}
                                    label="Пароль"
                                    placeholder="Введите пароль"
                                    error={errors?.password?.message}
                                />
                            )}
                            disabled={isLoading}
                        />
                    </Box>
                    <Box mb={2}>
                        <Controller
                            name="password2"
                            control={control}
                            disabled={isLoading}
                            render={({ field }) => (
                                <PasswordField
                                    {...field}
                                    label="Подтверждение"
                                    placeholder="Повторите пароль"
                                    error={errors?.password2?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box mb={2}>
                        <CustomAccordion title="Как создать пароль">
                            <Box>
                                <Typography
                                    variant="body2"
                                    color="primary"
                                >
                                    Для создания пароля разрешается применять следующие символы:
                                </Typography>
                                <ul style={{ listStyle: 'outside' }}>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            Заглавные латинские буквы: от A до Z (26 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            Строчные латинские буквы: от a до z (26 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            Цифры от 0 до 9 (10 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            {
                                                'Символы: (пробел) ! " # $ % & \' ( ) * + , - . / : ; < = > ? @ [  ] ^ _` { | } ~ (33 символа)'
                                            }
                                        </Typography>
                                    </li>
                                </ul>
                            </Box>
                        </CustomAccordion>
                    </Box>
                    {turnstileSiteKey && (
                        <Box
                            mb={2}
                            display="flex"
                            justifyContent="center"
                        >
                            <TurnstileWidget
                                key={turnstileResetKey}
                                siteKey={turnstileSiteKey}
                                onTokenChange={setCaptchaToken}
                            />
                        </Box>
                    )}
                    <Box mb={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={analyticsAccepted}
                                    disabled={isLoading}
                                    onChange={async (_event, checked) => {
                                        setAnalyticsAccepted(checked);
                                        await setAnalyticsConsentChoice(checked);
                                    }}
                                />
                            }
                            label="Разрешить аналитику, чтобы связать регистрацию с рекламой"
                        />
                    </Box>
                    <Box mb={2}>
                        <Button
                            disabled={isLoading || !isValid || Boolean(turnstileSiteKey && !captchaToken)}
                            variant="contained"
                            size="large"
                            type="submit"
                            fullWidth
                        >
                            Создать аккаунт
                        </Button>
                    </Box>
                    <Box>
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            textAlign="center"
                            fontWeight={400}
                        >
                            Нажимая «Создать аккаунт», вы принимаете следующие условия 
                            <Link
                                textAlign="center"
                                variant="caption"
                                color="primary"
                                component={'a'}
                                href={'https://tghunter.ru/terms'}
                                target="_blank"
                                fontWeight={400}
                            >
                                пользовательского соглашения
                            </Link>
                        </Typography>
                    </Box>
                </form>
            </WidgetWrapper>
            <Box
                mt={0.5}
                display="flex"
                justifyContent="center"
            >
                <Link
                    textAlign="center"
                    variant="caption"
                    color="primary"
                    component={RouterLink}
                    to={ROUTES.LOGIN.path}
                >
                    Есть аккаунт? Войти
                </Link>
            </Box>
        </LoadingWrapper>
    );
}
